import { findRelayModel } from '../services/relayModelKey';
import type { CoworkSession } from '../types/cowork';
import { readCoworkContextSnapshot, type CoworkContextState } from '../../shared/coworkContext';
import { findSessionModel, type ModelIdentity } from './sessionModel';

/** 纯展示模型；null 明确表示缺少可信窗口数据，不能显示为 0%。 */
export interface CoworkContextView {
  state: CoworkContextState;
  used: number | null;
  limit: number | null;
  modelId?: string;
}

/**
 * 只使用当前会话或根请求的模型身份计算窗口，不读取全局选择器。
 * @param session 当前会话及持久用量。
 * @param models 当前账号可用目录；重名或未知模型不猜测上限。
 */
export function describeCoworkContext(
  session: Pick<CoworkSession, 'modelId' | 'usage' | 'status' | 'origin'>,
  models: Array<ModelIdentity & { maxContext?: number }>,
): CoworkContextView {
  const context = readCoworkContextSnapshot(session.usage?.context);
  const modelId = context ? context.modelId : session.modelId ?? undefined;
  const model = session.origin === 'desktop' ? findRelayModel(models, modelId) : findSessionModel(models, modelId);
  const limit = model?.maxContext && Number.isSafeInteger(model.maxContext) && model.maxContext > 0 ? model.maxContext : null;
  let state = context?.state;
  // 压缩是执行态；进程重启或轮次结束后不能永久显示“正在压缩”。
  if (state === 'compacting' && session.status !== 'running') state = 'pending';
  if (state && state !== 'current') return { state, used: null, limit, modelId };
  const usage = session.usage;
  const counts = [usage?.inputTokens, usage?.cacheCreationTokens, usage?.cacheReadTokens];
  const valid = counts.every(value => Number.isSafeInteger(value) && (value as number) >= 0);
  const used = valid ? (counts as number[]).reduce((total, value) => total + value, 0) : null;
  if (!usage || usage.scope === 'turn' || limit === null || used === null || !Number.isSafeInteger(used) || used > limit) {
    return { state: 'unknown', used: null, limit, modelId };
  }
  return { state: 'current', used, limit, modelId };
}
